import React, { useState } from 'react'
import { Heart, Share2, Star, Users, DollarSign, TrendingUp, MessageCircle, Bell } from 'lucide-react'

interface CreatorHubProps {
  data: {
    creatorName: string
    creatorBio: string
    profileImage: string
    coverImage: string
    stats: {
      followers: number
      totalViews: number
      monthlyEarnings: number
      contentCount: number
    }
    content: Array<{
      id: string
      title: string
      type: 'video' | 'image' | 'post'
      thumbnail: string
      views: number
      likes: number
      isPaid: boolean
      price?: number
      publishedAt: string
    }>
    subscriptionTiers: Array<{
      id: string
      name: string
      price: number
      benefits: string[]
      subscriberCount: number
    }>
    blogPosts: Array<{
      id: string
      title: string
      excerpt: string
      publishedAt: string
      readTime: string
      category: string
    }>
    socialLinks: {
      youtube: string
      twitter: string
      instagram: string
      tiktok: string
    }
  }
}

export default function CreatorHub({ data }: CreatorHubProps) {
  const [activeTab, setActiveTab] = useState<'content' | 'blog' | 'subscriptions'>('content')
  const [likedContent, setLikedContent] = useState<Set<string>>(new Set())

  const toggleLike = (contentId: string) => {
    const newLikedContent = new Set(likedContent)
    if (newLikedContent.has(contentId)) {
      newLikedContent.delete(contentId)
    } else {
      newLikedContent.add(contentId)
    }
    setLikedContent(newLikedContent)
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-900 via-black to-green-900 text-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${data.coverImage || '/api/placeholder/1920/1080'})`,
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          {/* Profile Image */}
          <div className="mb-8">
            <img
              src={data.profileImage || '/api/placeholder/300/300'}
              alt={data.creatorName}
              className="w-48 h-48 rounded-full mx-auto border-4 border-gold-400 shadow-2xl"
            />
          </div>
          
          {/* Creator Name */}
          <h1 
            className="text-6xl md:text-8xl font-bold mb-6"
            style={{ 
              fontFamily: 'Georgia, serif',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              background: 'linear-gradient(45deg, #F59E0B, #10B981, #EF4444)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {data.creatorName}
          </h1>
          
          {/* Bio */}
          <p className="text-xl md:text-2xl text-orange-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            {data.creatorBio}
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-gold-400">{formatNumber(data.stats.followers)}</div>
              <div className="text-orange-200">Followers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">{formatNumber(data.stats.totalViews)}</div>
              <div className="text-orange-200">Total Views</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400">${data.stats.monthlyEarnings}</div>
              <div className="text-orange-200">Monthly Earnings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">{data.stats.contentCount}</div>
              <div className="text-orange-200">Content Pieces</div>
            </div>
          </div>
          
          {/* Social Links */}
          <div className="flex justify-center gap-6 mb-8">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-gold-500 hover:text-black transition-all duration-300"
              >
                {platform.charAt(0).toUpperCase()}
              </a>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="flex gap-4 justify-center">
            <button className="bg-gold-500 text-black px-8 py-4 rounded-full text-xl font-semibold hover:bg-gold-600 transition-colors flex items-center gap-3">
              <Users className="w-6 h-6" />
              Follow
            </button>
            <button className="bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full text-xl font-semibold hover:bg-white/30 transition-colors flex items-center gap-3">
              <Bell className="w-6 h-6" />
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 px-6 border-b border-white/20">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center gap-8">
            {[
              { id: 'content', label: 'Content', icon: <TrendingUp className="w-5 h-5" /> },
              { id: 'blog', label: 'Blog', icon: <MessageCircle className="w-5 h-5" /> },
              { id: 'subscriptions', label: 'Subscriptions', icon: <DollarSign className="w-5 h-5" /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-gold-500 text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      {activeTab === 'content' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-4xl font-bold text-center mb-12"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Latest Content
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {data.content.map((item) => (
                <div
                  key={item.id}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden hover:bg-white/15 transition-all duration-300 group"
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-gradient-to-br from-orange-500/20 to-green-500/20 flex items-center justify-center relative">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        {item.type === 'video' ? '▶️' : item.type === 'image' ? '🖼️' : '📝'}
                      </div>
                      <p className="text-white text-sm">{item.title}</p>
                    </div>
                    
                    {/* Paid Badge */}
                    {item.isPaid && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-gold-500 text-black px-2 py-1 rounded text-xs font-semibold">
                          ${item.price}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-2 line-clamp-2">{item.title}</h3>
                    
                    <div className="flex items-center justify-between text-sm text-orange-200 mb-4">
                      <span>{formatNumber(item.views)} views</span>
                      <span>{item.publishedAt}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleLike(item.id)}
                        className={`flex items-center gap-2 transition-colors ${
                          likedContent.has(item.id)
                            ? 'text-red-400'
                            : 'text-orange-200 hover:text-red-400'
                        }`}
                      >
                        <Heart className={`w-5 h-5 ${likedContent.has(item.id) ? 'fill-current' : ''}`} />
                        {likedContent.has(item.id) ? item.likes + 1 : item.likes}
                      </button>
                      
                      <div className="flex gap-2">
                        <button className="p-2 text-orange-200 hover:text-blue-400 transition-colors">
                          <Share2 className="w-4 h-4" />
                        </button>
                        {item.isPaid ? (
                          <button className="bg-gold-500 text-black px-4 py-2 rounded-lg hover:bg-gold-600 transition-colors text-sm">
                            Unlock (${item.price})
                          </button>
                        ) : (
                          <button className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm">
                            View
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Section */}
      {activeTab === 'blog' && (
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-4xl font-bold text-center mb-12"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Latest Blog Posts
            </h2>
            
            <div className="space-y-8">
              {data.blogPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-8 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-sm">
                      {post.category}
                    </span>
                    <span className="text-orange-200 text-sm">{post.publishedAt}</span>
                    <span className="text-orange-200 text-sm">{post.readTime}</span>
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-white mb-4">{post.title}</h3>
                  <p className="text-orange-200 mb-6 leading-relaxed">{post.excerpt}</p>
                  
                  <button className="text-gold-400 hover:text-gold-300 transition-colors font-semibold">
                    Read More →
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Subscriptions Section */}
      {activeTab === 'subscriptions' && (
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-4xl font-bold text-center mb-12"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Subscription Tiers
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {data.subscriptionTiers.map((tier) => (
                <div
                  key={tier.id}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-8 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-semibold text-white mb-2">{tier.name}</h3>
                    <div className="text-4xl font-bold text-gold-400 mb-2">${tier.price}</div>
                    <div className="text-orange-200 text-sm">per month</div>
                    <div className="text-orange-300 text-sm mt-2">
                      {formatNumber(tier.subscriberCount)} subscribers
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {tier.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center gap-3 text-orange-200">
                        <Star className="w-5 h-5 text-gold-400" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  
                  <button className="w-full bg-gold-500 text-black py-3 rounded-lg hover:bg-gold-600 transition-colors font-semibold">
                    Subscribe Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-200 hover:text-gold-400 transition-colors"
              >
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </a>
            ))}
          </div>
          <p className="text-orange-200">
            © 2024 {data.creatorName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
